import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, Plus, Trash2, Calendar, UserPlus, User, Download } from '../icons';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getWeddings, getChecklist, addChecklistItem, toggleChecklistItem, deleteChecklistItem, getCollaborators, assignChecklistItem } from '../services/api';

function Checklist() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [weddingId, setWeddingId] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', description: '', due_date: '', category: 'Planning' });
  const [filterCategory, setFilterCategory] = useState('all');
  const [helpers, setHelpers] = useState([]);
  const [assigningId, setAssigningId] = useState(null);
  // Mirrors the server rules in routes/checklist.js: owners and read/write
  // delegates manage everything, helpers tick/self-assign (and add if allowed),
  // read-only delegates only look.
  const [access, setAccess] = useState({ canEdit: false, canManage: false, canAdd: false, readOnly: false });
  const [addError, setAddError] = useState('');
  const [actionError, setActionError] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    let weddingData = null;
    setLoadError(false);
    getWeddings()
      .then((res) => {
        if (res.data.length > 0) {
          weddingData = res.data[0];
          setWeddingId(weddingData.id);
          setWedding(weddingData);
          return Promise.all([
            getChecklist(weddingData.id),
            getCollaborators(weddingData.id).catch(() => ({ data: { helpers: [], delegates: [] } })),
          ]);
        }
      })
      .then((results) => {
        if (results) {
          const allHelpers = results[1].data.helpers.filter(h => h.invite_status === 'accepted');
          setItems(results[0].data);
          setHelpers(allHelpers);
          if (weddingData?.user_id === user?.id) {
            setAccess({ canEdit: true, canManage: true, canAdd: true, readOnly: false });
          } else {
            const team = [...allHelpers, ...(results[1].data.delegates || [])];
            const me = team.find(c => c.user_id === user?.id && c.invite_status === 'accepted') || user?.collaborator;
            const role = me?.role || me?.collaborator_role;
            if (role === 'delegate') {
              const write = me.access_level === 'write';
              setAccess({ canEdit: write, canManage: write, canAdd: write, readOnly: !write });
            } else {
              const canView = me?.can_view_checklist !== false;
              setAccess({ canEdit: canView, canManage: false, canAdd: canView && me?.can_add_checklist === true, readOnly: false });
            }
          }
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [user, navigate, authLoading, reloadKey]);

  const showActionError = (err, fallback) => {
    setActionError(err.response?.data?.error || fallback);
  };

  const handleToggle = async (id) => {
    setActionError('');
    try {
      const res = await toggleChecklistItem(id);
      setItems(items.map(item => item.id === id ? { ...item, ...res.data } : item));
    } catch (err) {
      showActionError(err, t('coupleTools.actionFailed'));
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    try {
      const res = await addChecklistItem(weddingId, { ...newItem, sort_order: items.length });
      setItems([...items, res.data]);
      setNewItem({ title: '', description: '', due_date: '', category: 'Planning' });
      setShowAdd(false);
    } catch (err) {
      setAddError(err.response?.data?.error || t('coupleTools.actionFailed'));
    }
  };

  const handleRefresh = async () => {
    if (!weddingId) return;
    setRefreshing(true);
    setActionError('');
    try {
      const res = await getChecklist(weddingId);
      setItems(res.data);
    } catch (err) {
      showActionError(err, t('coupleTools.loadFailed'));
    }
    setRefreshing(false);
  };

  // Poll every 30s so couple & helper both see live additions
  useEffect(() => {
    if (!weddingId) return;
    const interval = setInterval(async () => {
      try {
        const res = await getChecklist(weddingId);
        setItems(res.data);
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, [weddingId]);

  const handleDelete = async (id) => {
    setActionError('');
    try {
      await deleteChecklistItem(id);
      setItems(items.filter(item => item.id !== id));
    } catch (err) {
      showActionError(err, t('coupleTools.actionFailed'));
    }
  };

  const handleAssign = async (itemId, userId) => {
    setActionError('');
    try {
      const res = await assignChecklistItem(itemId, { assignTo: userId || null });
      setItems(items.map(item => item.id === itemId ? { ...res.data, assigned_first_name: res.data.assigned_first_name, assigned_last_name: res.data.assigned_last_name } : item));
      setAssigningId(null);
    } catch (err) {
      showActionError(err, t('coupleTools.actionFailed'));
    }
  };

  const categories = [...new Set(items.map(i => i.category))].filter(Boolean);
  const filteredItems = filterCategory === 'all' ? items : items.filter(i => i.category === filterCategory);
  const completedCount = items.filter(i => i.is_completed).length;
  const progress = items.length ? Math.round((completedCount / items.length) * 100) : 0;

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  if (loadError || !wedding) {
    const isCouple = user?.role === 'couple';
    return (
      <div className="checklist-page">
        <div className="container">
          <div className="empty-state-sm">
            <CheckSquare size={40} />
            <h2>{loadError ? t('coupleTools.loadFailed') : t('coupleTools.noWeddingTitle')}</h2>
            {!loadError && <p>{isCouple ? t('coupleTools.noWeddingDesc') : t('coupleTools.noTeamDesc')}</p>}
            <div style={{ marginTop: 16 }}>
              {loadError ? (
                <button className="btn btn-primary" onClick={() => { setLoading(true); setReloadKey(k => k + 1); }}>{t('coupleTools.tryAgain')}</button>
              ) : isCouple && (
                <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>{t('coupleTools.goToDashboard')}</button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checklist-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1><CheckSquare size={28} /> {wedding ? `${wedding.partner1_name} & ${wedding.partner2_name}'s ` : ''}{t('checklist.title')}</h1>
            <p>{completedCount} {t('checklist.of')} {items.length} {t('checklist.tasksCompleted')}</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="btn btn-outline" onClick={handleRefresh} disabled={refreshing} title={t('checklist.refresh')}>
              {refreshing ? '...' : '↻'} {t('checklist.refresh')}
            </button>
            <button className="btn btn-outline" onClick={() => {
              const data = items.map(i => ({
                Title: i.title,
                Description: i.description || '',
                Category: i.category || '',
                'Due Date': i.due_date ? new Date(i.due_date).toLocaleDateString() : '',
                Status: i.is_completed ? 'Completed' : 'Pending',
                'Assigned To': i.assigned_first_name ? `${i.assigned_first_name} ${i.assigned_last_name || ''}`.trim() : ''
              }));
              const ws = XLSX.utils.json_to_sheet(data);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Checklist');
              XLSX.writeFile(wb, 'wedding-checklist.xlsx');
            }} disabled={items.length === 0}>
              <Download size={16} /> {t('checklist.exportExcel')}
            </button>
            {access.canAdd && (
              <button className="btn btn-primary" onClick={() => { setShowAdd(!showAdd); setAddError(''); }}>
                <Plus size={16} /> {t('checklist.addTask')}
              </button>
            )}
          </div>
        </div>

        {access.readOnly && <div className="alert alert-warning">{t('coupleTools.readOnlyNotice')}</div>}
        {actionError && (
          <div className="alert alert-error">
            {actionError}
            <button className="alert-close" onClick={() => setActionError('')}>&times;</button>
          </div>
        )}

        {/* Progress Bar */}
        <div className="checklist-progress">
          <div className="progress-header">
            <span>{progress}{t('checklist.complete')}</span>
            <span>{completedCount}/{items.length} {t('checklist.tasks')}</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        {/* Add Task Form */}
        {showAdd && (
          <form className="add-form" onSubmit={handleAdd}>
            <input
              type="text"
              placeholder={t('checklist.taskTitle')}
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder={t('checklist.descriptionOptional')}
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
            />
            <input
              type="date"
              value={newItem.due_date}
              onChange={(e) => setNewItem({ ...newItem, due_date: e.target.value })}
            />
            <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}>
              <option value="Planning">{t('checklist.planning')}</option>
              <option value="Venue">{t('checklist.venue')}</option>
              <option value="Catering">{t('checklist.catering')}</option>
              <option value="Photography">{t('checklist.photography')}</option>
              <option value="Attire">{t('checklist.attire')}</option>
              <option value="Ceremony">{t('checklist.ceremony')}</option>
              <option value="Pre-wedding">{t('checklist.preWedding')}</option>
              <option value="Entertainment">{t('checklist.entertainment')}</option>
              <option value="Beauty">{t('checklist.beauty')}</option>
              <option value="Invitations">{t('checklist.invitations')}</option>
              <option value="Guests">{t('checklist.guests')}</option>
            </select>
            {addError && <p style={{ color: '#e53e3e', fontSize: 13, margin: '4px 0 0' }}>{addError}</p>}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">{t('checklist.addTask')}</button>
              <button type="button" className="btn btn-outline" onClick={() => { setShowAdd(false); setAddError(''); }}>{t('common.cancel')}</button>
            </div>
          </form>
        )}

        {/* Category Filter */}
        <div className="category-tabs">
          <button className={filterCategory === 'all' ? 'active' : ''} onClick={() => setFilterCategory('all')}>
            {t('common.all')} ({items.length})
          </button>
          {categories.map(cat => (
            <button key={cat} className={filterCategory === cat ? 'active' : ''} onClick={() => setFilterCategory(cat)}>
              {cat} ({items.filter(i => i.category === cat).length})
            </button>
          ))}
        </div>

        {/* Checklist Items */}
        <div className="checklist-items">
          {filteredItems.length === 0 && (
            <div className="empty-state-sm">
              <p>{access.canAdd ? t('checklist.emptyCanAdd') : t('checklist.empty')}</p>
            </div>
          )}
          {filteredItems.map((item) => (
            <div key={item.id} className={`checklist-item ${item.is_completed ? 'completed' : ''}`}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={item.is_completed}
                  disabled={!access.canEdit}
                  onChange={() => handleToggle(item.id)}
                />
                <span className="checkmark"></span>
              </label>
              <div className="item-content">
                <h4>{item.title}</h4>
                {item.description && <p>{item.description}</p>}
                <div className="item-meta">
                  {item.category && <span className="item-category">{item.category}</span>}
                  {item.due_date && (
                    <span className="item-date">
                      <Calendar size={12} /> {new Date(item.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {item.assigned_to && (
                    <span className="item-assigned">
                      <User size={12} /> {item.assigned_first_name} {item.assigned_last_name}
                    </span>
                  )}
                </div>
              </div>
              <div className="item-actions">
                {!access.canEdit ? null : assigningId === item.id ? (
                  <div className="assign-dropdown">
                    <select
                      onChange={(e) => handleAssign(item.id, e.target.value ? parseInt(e.target.value) : null)}
                      defaultValue={item.assigned_to || ''}
                    >
                      <option value="">— {t('checklist.unassign')} —</option>
                      <option value={user?.id}>{user?.first_name} {user?.last_name} ({t('checklist.myself')})</option>
                      {/* the server only lets helpers assign tasks to themselves */}
                      {access.canManage && helpers
                        .filter(h => h.user_id !== user?.id)
                        .map(h => (
                          <option key={h.user_id} value={h.user_id}>
                            {h.first_name} {h.last_name}
                          </option>
                        ))
                      }
                    </select>
                    <button className="btn-xs" onClick={() => setAssigningId(null)}>✕</button>
                  </div>
                ) : (
                  <button className="icon-btn" title={item.assigned_to ? `${item.assigned_first_name || ''} ${item.assigned_last_name || ''}`.trim() : t('checklist.assignTask')} onClick={() => setAssigningId(item.id)}>
                    <UserPlus size={14} />
                    {item.assigned_to && <span style={{ marginLeft: 4, fontSize: 12 }}>{item.assigned_first_name}</span>}
                  </button>
                )}
                {(access.canManage || (access.canEdit && item.added_by === user?.id)) && (
                  <button className="delete-btn" onClick={() => handleDelete(item.id)}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Checklist;
